import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import Swal from 'sweetalert2';
import { EventsHubService } from '../events-hub.service';
import { AuthenticationService } from './authentication.service';
import { TemplateModalService } from './template-modal.service';

/**
 * Tells the user their permissions changed and then forces a logout, so a page they lost access to (or one that
 * gained a feature) is never left open on stale permissions.
 *
 * The prompt counts down and cannot be dismissed by clicking outside it or pressing Escape. When the countdown ends,
 * or the user closes the prompt early, they are logged out; a timer enforces the same deadline even if the prompt
 * never settles. Only one prompt runs at a time per tab, so a second detection while the countdown is running does
 * not restart it.
 */
@Injectable()
export class PermissionsChangedLogoutService {

  public static readonly LOGOUT_DELAY_MS = 15000;

  private promptInProgress = false;
  private fallbackTimer: ReturnType<typeof setTimeout> = null;

  constructor(
    private authenticationService: AuthenticationService,
    private templateModalService: TemplateModalService,
    private translateService: TranslateService,
    private eventsHubService: EventsHubService
  ) {}

  /**
   * Shows the "permissions changed" countdown prompt, then logs the user out.
   */
  public promptAndLogout(): void {
    if (this.promptInProgress) {
      return;
    }
    this.promptInProgress = true;

    // Swal is a singleton modal: if another component opens one over this prompt, this promise is not guaranteed
    // to settle. The logout must not depend on it, so a plain timer enforces the same deadline.
    this.fallbackTimer = setTimeout(() => this.logout(), PermissionsChangedLogoutService.LOGOUT_DELAY_MS);

    try {
      Swal.fire({
        ...this.templateModalService.getInactivityOptions(
          this.translateService.instant('KEEP_ALIVE.PERMISSIONS_UPDATED_TITLE'),
          this.translateService.instant('KEEP_ALIVE.PERMISSIONS_UPDATED_MESSAGE')
        ),
        confirmButtonText: this.translateService.instant('KEEP_ALIVE.PERMISSIONS_UPDATED_LOGOUT_NOW'),
        timer: PermissionsChangedLogoutService.LOGOUT_DELAY_MS,
        timerProgressBar: true,
        allowOutsideClick: false,
        allowEscapeKey: false
      }).then(() => this.logout(), () => this.logout());
    } catch {
      this.logout();
    }
  }

  /**
   * Logs out once per prompt, whichever of the prompt closing or the fallback timer comes first.
   */
  private logout(): void {
    if (!this.promptInProgress) {
      return;
    }
    this.promptInProgress = false;
    clearTimeout(this.fallbackTimer);
    this.fallbackTimer = null;
    this.eventsHubService.stopHeartBeat();
    this.authenticationService.logout();
  }
}
